import { EMAIL_REGEXP, maskPhoneNumber } from '@/lib/utils/form';
import { Watermark } from 'antd';
import React from 'react';
import {
  UserInfoProvider,
  useUserInfoContext,
} from '@/contexts/user-info-context';
import { useStores } from '@/store/hooks/useStore';

interface WatermarkContent {
  email?: string;
  phone?: string;
}

const convertAccountToWatermark = (account: string): WatermarkContent => {
  if (account.includes('@') && EMAIL_REGEXP.test(account)) {
    return {
      email: account,
    };
  } else {
    return {
      phone: account,
    };
  }
};
const WATERMARK_DEFAULTS = {
  zIndex: 99999,
  fontSize: 14,
  color: 'rgba(0, 0, 0, 0.08)',
} as const;
// 原始的 WithWatermark 组件，保持向后兼容
export default function WithWatermark({
  children,
  watermarkContent,
  zIndex = WATERMARK_DEFAULTS.zIndex,
  fontSize = WATERMARK_DEFAULTS.fontSize,
  color = WATERMARK_DEFAULTS.color,
}: {
  children: React.ReactNode;
  watermarkContent: WatermarkContent;
  zIndex?: number;
  fontSize?: number;
  color?: string;
}) {
  // 构建水印文本内容
  const buildWatermarkText = () => {
    if (!watermarkContent) return '';
    const { email, phone } = watermarkContent;
    if (!email && !phone) return '';

    const contactInfo = email ? email : phone ? maskPhoneNumber(phone) : '';

    // 返回两行内容的数组
    return contactInfo ? contactInfo : '';
  };

  const watermarkText = buildWatermarkText();
  const shouldRenderWatermark = watermarkText.length > 0;

  return shouldRenderWatermark ? (
    <Watermark
      content={watermarkText}
      zIndex={zIndex}
      font={{
        fontSize: fontSize,
        color: color,
      }}
      gap={[150, 450]}
    >
      {children}
    </Watermark>
  ) : (
    <>{children}</>
  );
}

// 用于已有UserInfoProvider的页面，保持向后兼容
export function SmartWatermark({
  children,
  account,
}: {
  children: React.ReactNode;
  account?: string; // 分享者信息，用于未登录时显示
}) {
  const { userInfo } = useUserInfoContext();
  const stores = useStores();

  // 确定要使用的水印内容
  const getWatermarkContent = (): WatermarkContent => {
    // 优先级：登录用户信息 > 分享者信息
    if (stores?.userStore?.userInfo && userInfo) {
      return userInfo;
    }
    if (account) {
      return convertAccountToWatermark(account);
    }
    return {};
  };

  const watermarkContent = getWatermarkContent();

  return (
    <WithWatermark watermarkContent={watermarkContent}>
      {children}
    </WithWatermark>
  );
}

// 用于没有UserInfoProvider的页面，自动包装
export function WatermarkWithProvider({
  children,
  account,
}: {
  children: React.ReactNode;
  account?: string;
}) {
  return (
    <UserInfoProvider>
      <SmartWatermark account={account}>{children}</SmartWatermark>
    </UserInfoProvider>
  );
}
